import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, HelpCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { api, type Paginated } from '../lib/api';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { usePeriod, type Period } from '../lib/period';
import type { AiView, EmployeeRow, Finding, NameRef, RowStatus, WorkMetrics } from '../lib/types';
import { Badge, Button, Card, CardHeader, Detail, EmptyState, PageHeader, Pagination, ProgressBar, Table, Tabs, Td, Th, Tr, type BadgeTone } from '../components/ui';
import { AiPanel, KpiCard, NotMeasurable, PeriodLine, SnapshotStatus, useNumber } from '../components/kit';
import { AiActions, WrittenForNote } from '../components/a-ai';
import { AXIS, ChartFrame, GRID, SERIES, TableTwin, dotStyle, tooltipItemStyle, tooltipLabelStyle, tooltipStyle } from '../components/b-charts';
import {
  BackLink,
  CodeBadge,
  Loaded,
  Note,
  SCORE_LABEL_TONE,
  Stat,
  StatGrid,
  StatusBadge,
  TextLink,
  TruncatedNote,
  evidenceIdHref,
  sourceHref,
  useAmount,
  useCode,
  useDates,
  useFilterOptions,
} from '../components/b-shared';

/**
 * One employee (CR-008 §6.4): their work score and how it was made — each dimension's ratio and formula,
 * the counts, the findings, the trend, the period before and the stored revisions — then the evidence behind
 * it, and, apart, their wallet. Never their salary or contact details. AI may explain a stored evaluation of
 * a closed day or month; it never sets or changes the score.
 */

const TABS = ['performance', 'evidence', 'points'] as const;
type Tab = (typeof TABS)[number];
const AI_WAITING = new Set(['PENDING', 'RUNNING']);

interface TrendPoint {
  start: string;
  end: string;
  score: number | null;
  reason: string | null;
  evidenceCount: number;
  measurableWeight: number;
}

interface Revision {
  id: string;
  revision: number;
  status: string;
  score: number | null;
  isCurrent: boolean;
  revisionReason: string | null;
  computedAt: string;
  finalizedAt: string | null;
  settingsVersion: number;
}

interface PerformancePayload {
  period: Period;
  settingsVersion: number;
  labels: { excellent: number; veryGood: number; good: number };
  user: { id: string; fullName: string; fullNameAr: string | null; jobTitle: string | null; isActive: boolean };
  departments: Array<NameRef & { isPrimary: boolean }>;
  row: EmployeeRow | null;
  previous: { score: number | null; reason: string | null; status: RowStatus } | null;
  trend: { bucket: 'DAY' | 'MONTH'; points: TrendPoint[] };
  revisions: Revision[];
  evidenceCount: number;
  truncated: boolean;
  ai: AiView;
}

export function EmployeeDetailPage() {
  const { userId = '' } = useParams();
  const { t, localized } = useI18n();
  const { params, param, setParam, query } = usePeriod();
  const tab: Tab = (TABS as readonly string[]).includes(param('tab')) ? (param('tab') as Tab) : 'performance';
  const filters = useFilterOptions();

  const performance = useQuery({
    queryKey: ['executive', 'employee', userId, 'performance', params],
    queryFn: () => api.get<PerformancePayload>(`/executive/employees/${userId}/performance`, { ...params }),
    enabled: !!userId,
    // Another period of the same person stays on screen while the new one loads — never another person's.
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[2] === userId ? previous : undefined),
    // An explanation being written is looked at again until it is ready.
    refetchInterval: (q) => (AI_WAITING.has(q.state.data?.ai?.status ?? '') ? 10_000 : false),
  });

  const listed = filters.data?.employees.find((e) => e.id === userId) ?? null;
  const person = performance.data?.user ?? listed;
  const departments = performance.data?.departments ?? [];
  const backSearch = new URLSearchParams(query);

  return (
    <>
      <BackLink to={`/employees?${backSearch.toString()}`}>{t('employee.back')}</BackLink>
      <PageHeader
        title={person ? localized(person) : performance.isLoading ? t('common.loading') : '—'}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {person?.jobTitle && <span>{person.jobTitle}</span>}
            {departments.map((d) => (
              <Badge key={d.id} tone={d.isPrimary ? 'brand' : 'neutral'} title={d.isPrimary ? t('employee.primaryDepartment') : undefined}>
                {localized(d)}
              </Badge>
            ))}
            {person && person.isActive === false && <Badge tone="warning">{t('common.formerEmployee')}</Badge>}
          </span>
        }
      />
      <Tabs tabs={TABS.map((id) => ({ id, label: t(`employee.tab.${id}`) }))} active={tab} onChange={(id) => setParam('tab', id === 'performance' ? null : id)} />
      <div className="mt-5">
        {tab === 'performance' && <Loaded query={performance}>{(data) => <PerformanceTab data={data} userId={userId} />}</Loaded>}
        {tab === 'evidence' && <EvidenceTab userId={userId} />}
        {tab === 'points' && <PointsTab userId={userId} />}
      </div>
    </>
  );
}

/* ============================================================================================== */
/* Performance                                                                                     */
/* ============================================================================================== */

function PerformanceTab({ data, userId }: { data: PerformancePayload; userId: string }) {
  const { t } = useI18n();
  const { setParam } = usePeriod();
  const row = data.row;
  return (
    <div className="space-y-5">
      <PeriodLine period={data.period} />
      <TruncatedNote truncated={data.truncated} />
      {!row ? (
        <Card>
          <EmptyState title={t('employee.noRow')} hint={t('employee.noRowHint')} />
        </Card>
      ) : (
        <>
          <div className="grid gap-5 lg:grid-cols-3">
            <ScoreCard data={data} row={row} />
            <PreviousCard data={data} row={row} />
          </div>
          <DimensionsCard row={row} />
          <CountsCard metrics={row.result.metrics} />
          <div className="grid gap-5 lg:grid-cols-3">
            <FindingsCard findings={row.result.findings} onEvidence={() => setParam('tab', 'evidence')} />
            <FlagsCard flags={row.result.flags} />
          </div>
        </>
      )}
      <TrendCard data={data} />
      <RevisionsCard data={data} />
      <ExplanationCard data={data} userId={userId} />
    </div>
  );
}

function ScoreCard({ data, row }: { data: PerformancePayload; row: EmployeeRow }) {
  const { t } = useI18n();
  const num = useNumber();
  const r = row.result;
  return (
    <Card className="lg:col-span-2">
      <CardHeader title={t('employee.score.title')} subtitle={t('employee.score.subtitle')} />
      <div className="flex flex-wrap items-start gap-x-8 gap-y-4">
        <div className="min-w-[9rem]">
          {r.score === null ? (
            <>
              <p className="text-metric text-[var(--text-primary)]">{t('work.na')}</p>
              <div className="mt-1">
                <NotMeasurable reason={r.reason} />
              </div>
              {r.reason && <p className="mt-1 max-w-[16rem] text-caption text-muted">{t(`employees.reason.${r.reason}.long`)}</p>}
            </>
          ) : (
            <>
              <p>
                <span dir="ltr" className="inline-flex items-baseline gap-1">
                  <span className="text-metric text-[var(--text-primary)]">{num(r.score, 0)}</span>
                  <span className="text-title text-muted">/100</span>
                </span>
              </p>
              {r.label && (
                <div className="mt-2">
                  <Badge tone={SCORE_LABEL_TONE[r.label]}>{t(`employees.label.${r.label}`)}</Badge>
                </div>
              )}
            </>
          )}
        </div>
        <dl className="min-w-[15rem] flex-1">
          <Detail label={t('employees.col.confidence')}>{t(`employees.confidence.${r.confidence}`)}</Detail>
          <Detail label={t('employees.col.measurable')}>
            <span className="tabular">{num(r.measurableWeight, 0)}%</span>
          </Detail>
          <Detail label={t('employees.col.evidence')}>
            <span className="tabular">{num(r.evidenceCount)}</span>
          </Detail>
          <Detail label={t('common.status')}>
            <SnapshotStatus status={row.status} />
          </Detail>
          <Detail label={t('employee.score.revision')}>{row.revision !== null ? t('employees.revision', { n: num(row.revision) }) : t('employee.score.notStored')}</Detail>
          <Detail label={t('employee.score.settingsVersion')}>
            <span className="tabular">{num(data.settingsVersion)}</span>
          </Detail>
        </dl>
      </div>
      <p className="mt-4 text-caption text-muted">
        {t('employee.score.labels', { excellent: num(data.labels.excellent), veryGood: num(data.labels.veryGood), good: num(data.labels.good) })}
      </p>
      <p className="mt-1 text-caption text-muted">{t('employee.score.confidenceHint')}</p>
    </Card>
  );
}

function PreviousCard({ data, row }: { data: PerformancePayload; row: EmployeeRow }) {
  const { t, has } = useI18n();
  const num = useNumber();
  const dates = useDates();
  const code = useCode();
  const previous = data.previous;
  const basisKey = `period.basis.${data.period.comparison.basis}`;
  const basis = has(basisKey) ? t(basisKey) : data.period.comparison.basis;
  const current = row.result.score;
  const diff = previous && previous.score !== null && current !== null ? Math.round((current - previous.score) * 10) / 10 : null;
  return (
    <Card>
      <CardHeader title={t('employee.previous.title')} subtitle={basis} />
      {!previous ? (
        <p className="text-body-sm text-muted">{t('employee.previous.none')}</p>
      ) : (
        <div className="space-y-2">
          {previous.score === null ? (
            <>
              <NotMeasurable reason={previous.reason} />
              {previous.reason && <p className="text-caption text-muted">{code('employees.reason', previous.reason)}</p>}
            </>
          ) : (
            <p>
              <span dir="ltr" className="inline-flex items-baseline gap-1">
                <span className="text-h1 text-[var(--text-primary)]">{num(previous.score, 0)}</span>
                <span className="text-body text-muted">/100</span>
              </span>
            </p>
          )}
          <div>
            <SnapshotStatus status={previous.status} />
          </div>
          <p className="text-body-sm text-[var(--text-secondary)]">
            {diff === null
              ? t('state.noComparison')
              : // A left-to-right isolate keeps the sign in front of the number in Arabic too.
                t('employee.previous.change', { value: `⁦${diff > 0 ? '+' : diff < 0 ? '−' : ''}${num(Math.abs(diff), 1)}⁩` })}
          </p>
        </div>
      )}
      <p className="mt-4 text-caption text-muted">
        <span dir="ltr" className="tabular">
          {dates.day(data.period.comparison.start)} → {dates.day(data.period.comparison.last)}
        </span>
      </p>
    </Card>
  );
}

function DimensionsCard({ row }: { row: EmployeeRow }) {
  const { t } = useI18n();
  const num = useNumber();
  return (
    <Card padded={false}>
      <div className="px-5 pt-5">
        <CardHeader title={t('employee.dimensions.title')} subtitle={t('employee.dimensions.subtitle')} />
      </div>
      <Table>
        <thead>
          <tr>
            <Th>{t('employee.dimensions.dimension')}</Th>
            <Th align="end">{t('employee.dimensions.weight')}</Th>
            <Th align="end">{t('employee.dimensions.ratio')}</Th>
            <Th>{t('employee.dimensions.score')}</Th>
            <Th>{t('employee.dimensions.formula')}</Th>
          </tr>
        </thead>
        <tbody>
          {row.result.dimensions.map((d) => (
            <Tr key={d.key}>
              <Td className="font-medium">{t(`dimension.${d.key}`)}</Td>
              <Td align="end" className="tabular">
                {d.enabled ? `${num(d.weight)}%` : <span className="text-muted">{t('employee.dimensions.off')}</span>}
              </Td>
              <Td align="end" className="tabular">
                {d.enabled ? (
                  <span dir="ltr">
                    {num(d.numerator)} / {num(d.denominator)}
                  </span>
                ) : (
                  '—'
                )}
              </Td>
              <Td className="min-w-[10rem]">
                {!d.enabled ? (
                  <span className="text-muted">{t('employee.dimensions.off')}</span>
                ) : d.measurable && d.score !== null ? (
                  <span className="flex items-center gap-2">
                    <span className="w-9 tabular">{num(d.score, 0)}</span>
                    <span className="block w-20">
                      <ProgressBar percent={d.score} />
                    </span>
                  </span>
                ) : (
                  <span className="text-caption text-muted">{t('employee.dimensions.notMeasurable')}</span>
                )}
              </Td>
              <Td className="min-w-[18rem] text-caption text-[var(--text-secondary)]">{t(`dimension.${d.key}.formula`)}</Td>
            </Tr>
          ))}
        </tbody>
      </Table>
      <div className="px-5 py-3">
        <Note>{t('employee.dimensions.note')}</Note>
      </div>
    </Card>
  );
}

const METRIC_GROUPS: Array<{ id: 'delivered' | 'deadlines' | 'review' | 'tickets' | 'collaboration'; keys: Array<keyof WorkMetrics> }> = [
  { id: 'delivered', keys: ['tasksDelivered', 'workflowSteps', 'qTasksDone', 'ticketsResolved'] },
  { id: 'deadlines', keys: ['finishedOnTime', 'finishedLate', 'dueMissed', 'blocked'] },
  { id: 'review', keys: ['approved', 'returned', 'awaitingReview', 'selfCompleted'] },
  { id: 'tickets', keys: ['ticketsReopened', 'viewSlaOnTime', 'viewSlaLate'] },
  { id: 'collaboration', keys: ['reviewsDone', 'reviewsDoneLate', 'reviewsOverdue'] },
];
const WATCH: ReadonlySet<keyof WorkMetrics> = new Set<keyof WorkMetrics>(['finishedLate', 'dueMissed', 'returned', 'ticketsReopened', 'viewSlaLate', 'reviewsDoneLate', 'reviewsOverdue']);

function CountsCard({ metrics }: { metrics: WorkMetrics }) {
  const { t } = useI18n();
  const num = useNumber();
  return (
    <Card>
      <CardHeader title={t('employee.metrics.title')} subtitle={t('employee.metrics.subtitle')} />
      <div className="space-y-4">
        {METRIC_GROUPS.map((g) => (
          <section key={g.id}>
            <h4 className="mb-2 text-caption font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">{t(`employee.metrics.${g.id}`)}</h4>
            <StatGrid>
              {g.keys.map((k) => (
                <Stat
                  key={k}
                  label={t(`employee.metric.${k}`)}
                  value={num(metrics[k] ?? 0)}
                  tone={WATCH.has(k) && (metrics[k] ?? 0) > 0 ? 'warning' : 'neutral'}
                  hint={k === 'blocked' ? t('employee.metric.blockedHint') : undefined}
                />
              ))}
            </StatGrid>
          </section>
        ))}
      </div>
    </Card>
  );
}

/** A finding in words: its code's sentence, with its figures — a dimension named, not keyed. */
function useFinding() {
  const { t, has } = useI18n();
  const num = useNumber();
  return (f: Finding) => {
    const vars: Record<string, string | number> = {};
    for (const [k, v] of Object.entries(f.params ?? {})) {
      if (typeof v === 'number') vars[k] = num(v);
      else {
        const dimensionKey = `dimension.${v}`;
        vars[k] = k === 'dimension' && has(dimensionKey) ? t(dimensionKey) : String(v);
      }
    }
    const key = `finding.${f.code}`;
    return has(key) ? t(key, vars) : f.code;
  };
}

const TONES = ['POSITIVE', 'CAUTION', 'UNCERTAIN'] as const;
const TONE_ICON: Record<(typeof TONES)[number], ReactNode> = {
  POSITIVE: <CheckCircle2 className="h-3.5 w-3.5 text-success-fg dark:text-success" aria-hidden />,
  CAUTION: <AlertTriangle className="h-3.5 w-3.5 text-warning-fg dark:text-warning-dark" aria-hidden />,
  UNCERTAIN: <HelpCircle className="h-3.5 w-3.5 text-[var(--text-tertiary)]" aria-hidden />,
};

function FindingsCard({ findings, onEvidence }: { findings: Finding[]; onEvidence: () => void }) {
  const { t } = useI18n();
  const say = useFinding();
  return (
    <Card className="lg:col-span-2">
      <CardHeader title={t('employee.findings.title')} subtitle={t('employee.findings.subtitle')} />
      {findings.length === 0 ? (
        <p className="text-body-sm text-muted">{t('employee.findings.none')}</p>
      ) : (
        <div className="grid gap-5 md:grid-cols-3">
          {TONES.map((tone) => {
            const mine = findings.filter((f) => f.tone === tone);
            return (
              <section key={tone}>
                <h4 className="mb-2 flex items-center gap-1.5 text-caption font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">
                  {TONE_ICON[tone]}
                  {t(`employee.findings.tone.${tone}`)}
                </h4>
                {mine.length === 0 ? (
                  <p className="text-caption text-muted">{t('employee.findings.noneOfTone')}</p>
                ) : (
                  <ul className="space-y-2">
                    {mine.map((f, i) => (
                      <li key={`${f.code}-${i}`} className="text-body-sm text-[var(--text-primary)]">
                        {say(f)}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      )}
      <div className="mt-4">
        <Button size="sm" variant="ghost" onClick={onEvidence}>
          {t('employee.findings.seeEvidence')}
        </Button>
      </div>
    </Card>
  );
}

function FlagsCard({ flags }: { flags: string[] }) {
  const { t, has } = useI18n();
  return (
    <Card>
      <CardHeader title={t('employee.flags.title')} subtitle={t('employee.flags.subtitle')} />
      {flags.length === 0 ? (
        <p className="text-body-sm text-muted">{t('employee.flags.none')}</p>
      ) : (
        <ul className="space-y-3">
          {flags.map((f) => {
            const key = `flag.${f}`;
            const hint = `flag.${f}.hint`;
            return (
              <li key={f}>
                <Badge tone="neutral">{has(key) ? t(key) : f}</Badge>
                {has(hint) && <p className="mt-1 text-caption text-muted">{t(hint)}</p>}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function TrendCard({ data }: { data: PerformancePayload }) {
  const { t } = useI18n();
  const num = useNumber();
  const dates = useDates();
  const code = useCode();
  const daily = data.trend.bucket === 'DAY';
  const points = data.trend.points;
  const rows = points.map((p) => ({ ...p, label: daily ? dates.shortDay(p.start) : dates.shortMonth(p.start) }));
  const scores = points.map((p) => p.score).filter((s): s is number => s !== null);
  const summary = scores.length
    ? t('employee.trend.summary', { scored: num(scores.length), total: num(points.length), min: num(Math.min(...scores), 0), max: num(Math.max(...scores), 0) })
    : t('employee.trend.allNa');
  const scoreName = t('employee.trend.score');
  return (
    <Card>
      <CardHeader title={t('employee.trend.title')} subtitle={daily ? t('employee.trend.byDay') : t('employee.trend.byMonth')} />
      {points.length === 0 ? (
        <EmptyState title={t('state.noData')} hint={t('employee.trend.emptyHint')} />
      ) : (
        <>
          {scores.length === 0 ? (
            <p className="py-10 text-center text-body-sm text-muted">{t('employee.trend.allNa')}</p>
          ) : (
            <ChartFrame label={summary}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke={GRID} vertical={false} />
                  <XAxis dataKey="label" {...AXIS} tickLine={false} minTickGap={18} />
                  <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} {...AXIS} width={34} tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    itemStyle={tooltipItemStyle}
                    labelStyle={tooltipLabelStyle}
                    formatter={(value) => [value === null || value === undefined ? t('state.notEnoughEvidence') : num(Number(value), 0), scoreName]}
                  />
                  <Line
                    type="linear"
                    dataKey="score"
                    name={scoreName}
                    stroke={SERIES[0]}
                    strokeWidth={2}
                    dot={dotStyle(SERIES[0])}
                    activeDot={{ r: 6, strokeWidth: 2, stroke: 'var(--surface-card)' }}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartFrame>
          )}
          <Note className="mt-2">{t('employee.trend.gaps')}</Note>
          <TableTwin summary={t('employee.trend.table')}>
            <Table>
              <thead>
                <tr>
                  <Th>{daily ? t('common.date') : t('employee.trend.month')}</Th>
                  <Th>{scoreName}</Th>
                  <Th align="end">{t('employees.col.evidence')}</Th>
                  <Th align="end">{t('employees.col.measurable')}</Th>
                </tr>
              </thead>
              <tbody>
                {points.map((p) => (
                  <Tr key={p.start}>
                    <Td>{daily ? dates.day(p.start) : dates.month(p.start)}</Td>
                    <Td>{p.score === null ? <span className="text-muted">{code('employees.reason', p.reason ?? 'INSUFFICIENT_EVIDENCE')}</span> : <span className="tabular">{num(p.score, 0)}</span>}</Td>
                    <Td align="end" className="tabular">
                      {num(p.evidenceCount)}
                    </Td>
                    <Td align="end" className="tabular">
                      {num(p.measurableWeight, 0)}%
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableTwin>
        </>
      )}
    </Card>
  );
}

function RevisionsCard({ data }: { data: PerformancePayload }) {
  const { t } = useI18n();
  const num = useNumber();
  const dates = useDates();
  const code = useCode();
  const tz = data.period.timezone;
  const storedKind = data.period.type === 'DAILY' || data.period.type === 'MONTHLY';
  return (
    <Card padded={false}>
      <div className="px-5 pt-5">
        <CardHeader title={t('employee.revisions.title')} subtitle={t('employee.revisions.subtitle')} />
      </div>
      {!storedKind ? (
        <p className="px-5 pb-5 text-body-sm text-muted">{t('employee.revisions.notStoredKind')}</p>
      ) : data.revisions.length === 0 ? (
        <p className="px-5 pb-5 text-body-sm text-muted">{data.period.live ? t('employee.revisions.live') : t('employee.revisions.none')}</p>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>{t('employee.revisions.revision')}</Th>
              <Th>{t('common.status')}</Th>
              <Th align="end">{t('employees.col.score')}</Th>
              <Th>{t('employee.revisions.why')}</Th>
              <Th>{t('employee.revisions.computed')}</Th>
              <Th>{t('employee.revisions.finalized')}</Th>
              <Th align="end">{t('employee.score.settingsVersion')}</Th>
            </tr>
          </thead>
          <tbody>
            {data.revisions.map((r) => (
              <Tr key={r.id}>
                <Td>
                  <span className="inline-flex flex-wrap items-center gap-1.5">
                    <span className="tabular">{t('employees.revision', { n: num(r.revision) })}</span>
                    {r.isCurrent && <Badge tone="brand">{t('employee.revisions.current')}</Badge>}
                  </span>
                </Td>
                <Td>
                  <SnapshotStatus status={r.status} />
                </Td>
                <Td align="end" className="tabular">
                  {r.score === null ? <span className="text-muted">{t('work.na')}</span> : num(r.score, 0)}
                </Td>
                <Td className="text-[var(--text-secondary)]">{r.revisionReason ? code('employee.revisions.reason', r.revisionReason) : t('employee.revisions.first')}</Td>
                <Td className="whitespace-nowrap">{dates.at(r.computedAt, tz)}</Td>
                <Td className="whitespace-nowrap">{r.finalizedAt ? dates.at(r.finalizedAt, tz) : '—'}</Td>
                <Td align="end" className="tabular">
                  {num(r.settingsVersion)}
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

function ExplanationCard({ data, userId }: { data: PerformancePayload; userId: string }) {
  const { t } = useI18n();
  const { params, query } = usePeriod();
  const queryClient = useQueryClient();
  const closed = !data.period.live && (data.period.type === 'DAILY' || data.period.type === 'MONTHLY');
  // Saved first: a saved explanation is shown as saved; asking again returns it — only Regenerate asks AI again.
  const action = closed ? (
    <AiActions
      ai={data.ai}
      askPath={`/executive/employees/${userId}/explanation`}
      askParams={{ ...params }}
      onChanged={() => void queryClient.invalidateQueries({ queryKey: ['executive', 'employee', userId, 'performance'] })}
    />
  ) : undefined;

  return (
    <div className="space-y-2">
      <AiPanel
        ai={data.ai}
        title={t('employee.ai.title')}
        action={action}
        evidenceLink={(id) => {
          const source = data.ai.evidenceMap?.[id];
          return source ? evidenceIdHref(source, query) : null;
        }}
      />
      <WrittenForNote ai={data.ai} revision={data.row?.revision ?? null} />
      {!closed && <Note>{t('employee.ai.closedOnly')}</Note>}
      <Note>
        {t('employee.ai.neverScore')} {t('aiAct.savedFirst')}
      </Note>
    </div>
  );
}

/* ============================================================================================== */
/* Evidence                                                                                        */
/* ============================================================================================== */

interface EvidenceRow {
  id: string;
  kind: string;
  sourceType: 'TASK' | 'Q_TASK' | 'TICKET';
  sourceId: string;
  at: string;
  dueAt?: string | null;
  onTime?: boolean | null;
  stayed?: boolean;
  verification?: string;
  priority?: string | null;
  source: { code: string | null; title: string; status: string } | null;
  project: { id: string; code: string; name: string } | null;
}

interface EvidencePayload extends Paginated<EvidenceRow> {
  period: Period;
}

function outcomeOf(e: EvidenceRow): { key: TranslationKey; tone: BadgeTone } {
  switch (e.kind) {
    case 'TICKET_RESOLVED':
      return e.stayed === false ? { key: 'evidence.outcome.reopened', tone: 'error' } : { key: 'evidence.outcome.stayed', tone: 'success' };
    case 'DUE_MISSED':
      return { key: 'evidence.outcome.missed', tone: 'error' };
    case 'BLOCKED_CONTEXT':
      return { key: 'evidence.outcome.blocked', tone: 'neutral' };
    case 'REVIEW_APPROVED':
      return { key: 'evidence.outcome.approved', tone: 'success' };
    case 'REVIEW_RETURNED':
      return { key: 'evidence.outcome.returned', tone: 'warning' };
    case 'REVIEW_OVERDUE':
      return { key: 'evidence.outcome.reviewWaiting', tone: 'warning' };
    case 'REVIEW_DONE':
      return e.onTime === false ? { key: 'evidence.outcome.reviewLate', tone: 'warning' } : { key: 'evidence.outcome.reviewInTime', tone: 'success' };
    case 'VIEW_SLA':
      return e.onTime ? { key: 'evidence.outcome.viewedInTime', tone: 'success' } : { key: 'evidence.outcome.viewedLate', tone: 'error' };
    default:
      if (e.onTime === true) return { key: 'evidence.outcome.onTime', tone: 'success' };
      if (e.onTime === false) return { key: 'evidence.outcome.late', tone: 'error' };
      return { key: 'evidence.outcome.noDeadline', tone: 'neutral' };
  }
}

function EvidenceTab({ userId }: { userId: string }) {
  const { t } = useI18n();
  const { params, param, setParam, query } = usePeriod();
  const dates = useDates();
  const code = useCode();
  const page = Math.max(1, Number(param('page')) || 1);
  const evidence = useQuery({
    queryKey: ['executive', 'employee', userId, 'evidence', params, page],
    queryFn: () => api.get<EvidencePayload>(`/executive/employees/${userId}/evidence`, { ...params, page }),
    enabled: !!userId,
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[2] === userId ? previous : undefined),
  });
  return (
    <Loaded query={evidence}>
      {(data) => {
        const tz = data.period.timezone;
        return (
          <div className="space-y-4">
            <PeriodLine period={data.period} />
            <Note>{t('evidence.listNote')}</Note>
            <Card padded={false}>
              {data.items.length === 0 ? (
                <EmptyState title={t('evidence.empty')} hint={t('evidence.emptyHint')} />
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <Th>{t('evidence.col.when')}</Th>
                      <Th>{t('evidence.col.kind')}</Th>
                      <Th>{t('evidence.col.record')}</Th>
                      <Th>{t('common.project')}</Th>
                      <Th>{t('evidence.col.deadline')}</Th>
                      <Th>{t('evidence.col.outcome')}</Th>
                      <Th>{t('evidence.col.verification')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((e) => {
                      const href = sourceHref(e.sourceType, e.sourceId, query);
                      const outcome = outcomeOf(e);
                      const label = e.source ? (
                        <>
                          {e.source.code && (
                            <span dir="ltr" className="me-1.5 font-mono text-caption text-[var(--text-tertiary)]">
                              {e.source.code}
                            </span>
                          )}
                          {e.source.title}
                        </>
                      ) : (
                        <span className="text-muted">{t('evidence.recordGone')}</span>
                      );
                      return (
                        <Tr key={e.id}>
                          <Td className="whitespace-nowrap">{dates.at(e.at, tz)}</Td>
                          <Td className="min-w-[9rem]">{code('evidence.kind', e.kind)}</Td>
                          <Td className="min-w-[14rem]">
                            {href && e.source ? <TextLink to={href}>{label}</TextLink> : label}
                            <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-caption text-[var(--text-tertiary)]">
                              {code('evidence.source', e.sourceType)}
                              {e.source && <StatusBadge status={e.source.status} />}
                            </span>
                          </Td>
                          <Td className="min-w-[8rem] text-[var(--text-secondary)]">
                            {e.project ? (
                              <>
                                <span dir="ltr" className="me-1.5 font-mono text-caption">
                                  {e.project.code}
                                </span>
                                {e.project.name}
                              </>
                            ) : (
                              '—'
                            )}
                          </Td>
                          <Td className="whitespace-nowrap">{e.dueAt ? dates.at(e.dueAt, tz) : '—'}</Td>
                          <Td>
                            <Badge tone={outcome.tone}>{t(outcome.key)}</Badge>
                          </Td>
                          <Td className="text-[var(--text-secondary)]">{e.verification ? code('evidence.verification', e.verification) : '—'}</Td>
                        </Tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
              <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={(p) => setParam('page', String(p))} />
            </Card>
          </div>
        );
      }}
    </Loaded>
  );
}

/* ============================================================================================== */
/* Points — the wallet, apart from performance                                                     */
/* ============================================================================================== */

interface EmployeePoints {
  period: Period;
  currentBalance: number;
  openingBalance: number;
  closingBalance: number;
  ledger: Array<{ id: string; at: string; kind: string; points: number; balanceAfter: number; source: string | null; redemption: { type: string; status: string } | null }>;
  redemptions: Array<{
    id: string;
    type: string;
    status: string;
    points: number;
    redeemedAt: string;
    completedAt: string | null;
    cancelledAt: string | null;
    cash: { status: string; amount: number; currency: string; paidAt: string | null } | null;
  }>;
  dayRewards: Array<{ id: string; days: number; status: string; month: string; grantedAt: string; paidAt: string | null; cancelledAt: string | null }>;
}

function PointsTab({ userId }: { userId: string }) {
  const { t } = useI18n();
  const { params } = usePeriod();
  const num = useNumber();
  const dates = useDates();
  const code = useCode();
  const amount = useAmount();
  const wallet = useQuery({
    queryKey: ['executive', 'employee', userId, 'points', params],
    queryFn: () => api.get<EmployeePoints>(`/executive/employees/${userId}/points`, { ...params }),
    enabled: !!userId,
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[2] === userId ? previous : undefined),
  });
  const signed = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${num(Math.abs(n))}`;
  return (
    <Loaded query={wallet}>
      {(data) => {
        const tz = data.period.timezone;
        return (
          <div className="space-y-5">
            <PeriodLine period={data.period} />
            <Card className="border-warning/40">
              <div className="space-y-1.5">
                <Note>{t('points.separate')}</Note>
                <Note>{t('points.private')}</Note>
                <Note>{t('points.daysOnly')}</Note>
              </div>
            </Card>
            <div className="grid gap-4 sm:grid-cols-3">
              <KpiCard label={t('points.balance.current')} value={num(data.currentBalance)} hint={t('points.balance.currentHint')} />
              <KpiCard label={t('points.balance.opening')} value={num(data.openingBalance)} hint={t('points.balance.openingHint')} />
              <KpiCard label={t('points.balance.closing')} value={num(data.closingBalance)} hint={t('points.balance.closingHint')} />
            </div>

            <Card padded={false}>
              <div className="px-5 pt-5">
                <CardHeader title={t('points.ledger.title')} subtitle={t('points.ledger.subtitle')} />
              </div>
              {data.ledger.length === 0 ? (
                <EmptyState title={t('points.ledger.empty')} hint={t('common.noResultsHint')} />
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <Th>{t('evidence.col.when')}</Th>
                      <Th>{t('points.ledger.entry')}</Th>
                      <Th>{t('points.ledger.source')}</Th>
                      <Th align="end">{t('points.ledger.points')}</Th>
                      <Th align="end">{t('points.ledger.balanceAfter')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.ledger.map((l) => (
                      <Tr key={l.id}>
                        <Td className="whitespace-nowrap">{dates.at(l.at, tz)}</Td>
                        <Td>{code('points.kind', l.kind)}</Td>
                        <Td className="text-[var(--text-secondary)]">
                          {l.source
                            ? code('points.source', l.source)
                            : l.redemption
                              ? `${code('points.redemption', l.redemption.type)} · ${code('points.redemptionStatus', l.redemption.status)}`
                              : '—'}
                        </Td>
                        <Td align="end" className={l.points < 0 ? 'tabular text-error-fg dark:text-error-dark' : 'tabular text-success-fg dark:text-success'}>
                          <span dir="ltr">{signed(l.points)}</span>
                        </Td>
                        <Td align="end" className="tabular">
                          {num(l.balanceAfter)}
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card>

            <Card padded={false}>
              <div className="px-5 pt-5">
                <CardHeader title={t('points.redemptions.title')} subtitle={t('points.redemptions.subtitle')} />
              </div>
              {data.redemptions.length === 0 ? (
                <EmptyState title={t('points.redemptions.empty')} hint={t('common.noResultsHint')} />
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <Th>{t('points.redemptions.type')}</Th>
                      <Th>{t('common.status')}</Th>
                      <Th align="end">{t('points.ledger.points')}</Th>
                      <Th>{t('points.redemptions.redeemedAt')}</Th>
                      <Th>{t('points.redemptions.completedAt')}</Th>
                      <Th>{t('points.redemptions.cash')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.redemptions.map((r) => (
                      <Tr key={r.id}>
                        <Td className="min-w-[12rem]">{code('points.redemption', r.type)}</Td>
                        <Td>
                          <CodeBadge prefix="points.redemptionStatus" value={r.status} />
                        </Td>
                        <Td align="end" className="tabular">
                          {num(r.points)}
                        </Td>
                        <Td className="whitespace-nowrap">{dates.at(r.redeemedAt, tz)}</Td>
                        <Td className="whitespace-nowrap">
                          {r.cancelledAt ? (
                            <span className="text-muted">{t('points.redemptions.cancelledOn', { date: dates.at(r.cancelledAt, tz) })}</span>
                          ) : r.completedAt ? (
                            dates.at(r.completedAt, tz)
                          ) : (
                            '—'
                          )}
                        </Td>
                        <Td className="min-w-[12rem]">
                          {r.cash ? (
                            <span className="flex flex-col gap-0.5">
                              <span className="tabular">{amount(r.cash.amount, r.cash.currency)}</span>
                              <span className="flex flex-wrap items-center gap-1.5 text-caption text-muted">
                                <CodeBadge prefix="points.cashStatus" value={r.cash.status} />
                                {r.cash.status === 'PAID' && r.cash.paidAt ? t('points.cash.paidOn', { date: dates.at(r.cash.paidAt, tz) }) : r.cash.status === 'PENDING' ? t('points.cash.notPaid') : null}
                              </span>
                            </span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card>

            <Card padded={false}>
              <div className="px-5 pt-5">
                <CardHeader title={t('points.dayRewards.title')} subtitle={t('points.dayRewards.subtitle')} />
              </div>
              {data.dayRewards.length === 0 ? (
                <EmptyState title={t('points.dayRewards.empty')} hint={t('common.noResultsHint')} />
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <Th align="end">{t('points.dayRewards.days')}</Th>
                      <Th>{t('common.status')}</Th>
                      <Th>{t('points.dayRewards.month')}</Th>
                      <Th>{t('points.dayRewards.grantedAt')}</Th>
                      <Th>{t('points.dayRewards.settled')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.dayRewards.map((d) => (
                      <Tr key={d.id}>
                        <Td align="end" className="tabular">
                          {num(d.days, 2)}
                        </Td>
                        <Td>
                          <CodeBadge prefix="points.dayRewardStatus" value={d.status} />
                        </Td>
                        <Td>{dates.month(d.month)}</Td>
                        <Td className="whitespace-nowrap">{dates.at(d.grantedAt, tz)}</Td>
                        <Td className="whitespace-nowrap">
                          {d.paidAt
                            ? t('points.cash.paidOn', { date: dates.at(d.paidAt, tz) })
                            : d.cancelledAt
                              ? t('points.redemptions.cancelledOn', { date: dates.at(d.cancelledAt, tz) })
                              : '—'}
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card>
          </div>
        );
      }}
    </Loaded>
  );
}
